/**
 * Notification Service
 * Handles notification/message CRUD operations
 */

import { apiClient, handleApiError } from './apiClient';
import { MESSAGES_API_BASE_URL, USERS_API_BASE_URL } from '../constants';
import {
  CreateNotificationRequest,
  CreateNotificationResponse,
  EditNotificationResponse,
} from '../types/Notification';

/**
 * Fetch notification threads with filtering and pagination
 * @param channels - Comma-separated channel names (app, email, sms, web)
 * @param search - Search query string
 * @param authToken - Authentication token
 * @param offset - Pagination offset (default: 0)
 * @returns Promise with filtered notification threads
 * @throws ApiError if request fails
 */
export const getNotificationThreads = async (
  channels: string,
  search: string,
  authToken: string,
  offset = 0
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/notification-threads/filtered-result/${offset}`,
      {
        params: { search, channels },
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get notification messages by notification thread UUID
 * @param notificationThreadUuid - Notification thread UUID
 * @param authToken - Authentication token
 * @returns Promise with group messages
 * @throws ApiError if request fails
 */
export const getNotificationByNotificationThreadUuid = async (
  notificationThreadUuid: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${MESSAGES_API_BASE_URL}/group-messages/${notificationThreadUuid}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Create new notification/message
 * @param postData - Notification creation data
 * @param authToken - Authentication token
 * @returns Promise with created notification
 * @throws ApiError if creation fails
 */
export const createNewNotification = async (
  postData: CreateNotificationRequest,
  authToken: string
): Promise<CreateNotificationResponse> => {
  try {
    // make sure the value of title in the json payload is not empty or just "-#DRFT#"
    if (!postData.title || postData.title === '-#DRFT#') {
      postData.title = 'Draft Notification';
    }
    const response = await apiClient.post<CreateNotificationResponse>(
      `${MESSAGES_API_BASE_URL}/message`,
      postData,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Edit existing notification/message
 * @param id - Message ID
 * @param postData - Updated notification data (partial)
 * @param authToken - Authentication token
 * @returns Promise with edit response
 * @throws ApiError if edit fails
 */
export const editNotification = async (
  id: string,
  postData: Partial<CreateNotificationRequest>,
  authToken: string
): Promise<EditNotificationResponse> => {
  try {
    const response = await apiClient.put<EditNotificationResponse>(
      `${MESSAGES_API_BASE_URL}/${id}`,
      postData,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Delete/remove a message
 * @param messageId - Message ID to delete
 * @param authToken - Authentication token
 * @returns Promise with deletion response
 * @throws ApiError if deletion fails
 */
export const removeMessage = async (
  messageId: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.delete(
      `${MESSAGES_API_BASE_URL}/message/${messageId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Send a draft message
 * @param messageUuid - Message UUID to send
 * @param authToken - Authentication token
 * @returns Promise with send response
 * @throws ApiError if send fails
 */
export const sendMessage = async (
  messageUuid: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${MESSAGES_API_BASE_URL}/send-message/${messageUuid}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get latest messages for groups
 * @param postData - Request data with group IDs
 * @param authToken - Authentication token
 * @returns Promise with latest messages
 * @throws ApiError if request fails
 */
export const getLatestMessagesForGroups = async (
  postData: any,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${MESSAGES_API_BASE_URL}/latest-messages-for-groups`,
      postData,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Create pinned message
 * @param postData - Pin message data
 * @param authToken - Authentication token
 * @returns Promise with pin response
 * @throws ApiError if pin creation fails
 */
export const createPinMessage = async (
  postData: any,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${MESSAGES_API_BASE_URL}/pin`,
      postData,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: authToken,
        },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Get pinned message for organization
 * @param orgId - Organization ID
 * @param authToken - Authentication token
 * @returns Promise with pinned message or empty array if orgId not provided
 * @throws ApiError if request fails
 */
export const getPinnedMessage = async (
  orgId: string,
  authToken: string
): Promise<any> => {
  if (!orgId) {
    console.warn('[Notification Service] OrgId is required to fetch pinned message');
    return { data: [] };
  }

  try {
    const response = await apiClient.get(
      `${MESSAGES_API_BASE_URL}/pinned-message/${orgId}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Add message read status/recipient
 * @param postData - Message recipient data
 * @param authToken - Authentication token
 * @returns Promise with add response
 * @throws ApiError if request fails
 */
export const addMessageReadStatus = async (
  postData: any,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${MESSAGES_API_BASE_URL}/addMessageRecepient`,
      postData,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Update last modified timestamp of notification thread
 * @param threadUuid - Thread UUID
 * @param authToken - Authentication token
 * @returns Promise with update response
 * @throws ApiError if update fails
 */
export const updateThreadTimestamp = async (
  threadUuid: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.get(
      `${USERS_API_BASE_URL}/notification-threads/update-thread-modified-time/${threadUuid}`,
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

/**
 * Resend notification with optional message
 * @param originalMessageUuid - UUID of original message to resend
 * @param optionalMessage - Optional text message to include (max 100 chars)
 * @param authToken - Authentication token
 * @returns Promise with resend response
 * @throws ApiError if resend fails
 */
export const resendNotification = async (
  originalMessageUuid: string,
  optionalMessage: string,
  authToken: string
): Promise<any> => {
  try {
    const response = await apiClient.post(
      `${MESSAGES_API_BASE_URL}/resend/${originalMessageUuid}`,
      { optionalMessage },
      {
        headers: { Authorization: authToken },
      }
    );
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};
